/**
 * Founder 2026-10-27 — one source for "what do I do about this update".
 * The Sites card band, the Settings -> Install callout and the Overview
 * banner all read it, so the three never disagree.
 *
 * Three cases:
 *   - WordPress plugin on 1.23+ updates from WordPress itself (ADR-0258).
 *   - WordPress plugin below 1.23 (or unknown) needs one last upload.
 *   - Cloudflare Worker: Redeploy on the Cloudflare card.
 */

import type { SiteInstallUpdate } from '@sill/types';
import { compareInstallTemplateVersions } from '@sill/types';

/** First plugin version that receives updates through WordPress. */
export const PLUGIN_WORDPRESS_UPDATES_FROM = '1.23';

export type InstallUpdateAction = 'wordpress' | 'upload' | 'redeploy';

export interface InstallUpdateCopy {
  readonly action: InstallUpdateAction;
  /** "WordPress plugin" / "Cloudflare Worker". */
  readonly install: string;
  /** "1.23 → 1.26", or "→ 1.26" when the running version is unknown. */
  readonly versions: string;
  /** One short line for the Sites-card band. e.g. "Plugin update: 1.23 → 1.26". */
  readonly band: string;
  /** One line for the Sites card (kept for callers that still read it). */
  readonly short: string;
  /** Ordered steps for Settings -> Install. At most two. */
  readonly steps: readonly string[];
}

export function installUpdateCopy(u: SiteInstallUpdate): InstallUpdateCopy {
  const versions = u.current !== null ? `${u.current} → ${u.latest}` : `→ ${u.latest}`;
  if (u.kind === 'worker') {
    return {
      action: 'redeploy',
      install: 'Cloudflare Worker',
      versions,
      band: `Worker update: ${versions}`,
      short: 'Redeploy the Cloudflare Worker from Settings → Install.',
      steps: [
        'Click Redeploy on the Cloudflare card below.',
        'The site stays up while it runs.',
      ],
    };
  }
  const viaWordPress = u.current !== null
    && compareInstallTemplateVersions(u.current, PLUGIN_WORDPRESS_UPDATES_FROM) >= 0;
  if (viaWordPress) {
    return {
      action: 'wordpress',
      install: 'WordPress plugin',
      versions,
      band: `Plugin update: ${versions}`,
      short: 'Update it in WordPress → Plugins.',
      steps: [
        'In WordPress, go to Dashboard → Updates and update Sill Agent Ready.',
        'Turn on auto-updates on the Plugins page so this happens on its own.',
      ],
    };
  }
  return {
    action: 'upload',
    install: 'WordPress plugin',
    versions,
    band: `Plugin update: ${versions}`,
    short: 'Upload the new plugin once. Later updates come through WordPress.',
    steps: [
      'Download the plugin below.',
      'In WordPress → Plugins → Add New → Upload Plugin, choose the file and replace.',
    ],
  };
}
